import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {FilterCriteria} from '../../../../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../../../../core/shared/misc/sorting';
import {ObjectRequestList} from '../../../../../../../../core/shared/dto/object/request/object-request-list';
import {
    ObjectRequestComplex
} from '../../../../../../../../core/shared/dto/object/request/object-request-complex';
import {
    ObjectRequestComplexNode
} from '../../../../../../../../core/shared/dto/object/request/object-request-complex-node';
import {ObjectIdentifier} from '../../../../../../../../core/shared/basic/object-identifier';
import {MvsCoreService} from '../../../../../../../../core/shared/service/mvs-core.service';
import {MvsObjectNavigationService} from '../../../../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {CwStepLevelEnum} from '../../../../../../model/dto/enum/process/cw-step-level.enum';
import {
    isQuickQuoteOfferableStepComponent
} from '../../../../../../util/cw-quick-quote-step-type.util';

/**
 * A single CwCaseTypeStep row projected onto the modelling timeline. Only the display-relevant
 * attributes are typed; the underlying data is a generic DtoDetail from the CwCaseTypeStep CRUD.
 */
interface CwStepRow {
    id: number;
    name: string;
    stepKey: string;
    stepOrder: number;
    level: number; // CwStepLevelEnum ordinal (0 CASE / 1 PRODUCT)
    optionalStep: boolean;
    visibilityLogicDtoId: number;
    ufFlowTypeDtoId: number;
    caseTypeProductDtoId: number;

    /**
     * The pinned product line's readable name, resolved on the client — see
     * {@code loadProductLines}. Unset until that read has answered, which is what keeps the badge
     * from flashing the id fallback first.
     *
     * <p>NOT read from a {@code caseTypeProductDtoName}: that field exists on the wire but carries
     * the ID AS A STRING, because {@code CwCaseTypeProductAccess.getText()} returns the id (same for
     * {@code ProductAccess}). Binding it would print "42" where a product name belongs — the trap
     * {@code CwCaseProductDto}'s class doc records for the very same relation.
     */
    productLabel?: string;
}

/**
 * A single CwCaseTypeQuickQuoteStep row of the Quick-Quote section.
 *
 * <p>A SEPARATE shape from {@link CwStepRow} rather than a reuse: the Quick Quote sister entity
 * deliberately has no {@code optionalStep}, no {@code visibilityLogic}, no {@code ufFlowType} and no
 * {@code sourceScope}, so a shared interface would carry four fields that are permanently undefined
 * and four badges that can never appear. It adds the one thing the regular row does not need — the
 * step TYPE's name, because the Quick Quote stack is read from the configuration alone and the step
 * type is what decides which component it renders.
 */
interface CwQuickQuoteStepRow {
    id: number;
    name: string;
    stepKey: string;
    stepOrder: number;
    level: number; // CwStepLevelEnum ordinal (0 CASE / 1 PRODUCT)
    active: boolean;
    caseTypeProductDtoId: number;
    stepTypeDtoId: number;

    /** Resolved on the client from the step-type catalogue — see {@code loadQuickQuoteSteps}. */
    stepTypeLabel?: string;

    /**
     * True when the configured step type is OUTSIDE what a Quick Quote can run — the backend's
     * capture verb will SKIP it, so the step contributes nothing to the premium.
     *
     * <p>The editor's picker never OFFERS such a type, so this cannot be produced by clicking. It can
     * be produced by everything else that writes configuration: a seed bundle, the generic CRUD form,
     * a direct API call, or a step type whose component this frontend has since stopped registering.
     * That is exactly why the timeline has to say so — otherwise the mismatch is discoverable only at
     * RUNTIME, in the capture surface's skipped-steps notice, by whoever is trying to get a premium
     * rather than by whoever configured it.
     *
     * <p>{@code undefined} while the catalogue has not answered: no claim is made from a read that has
     * not returned, so a slow or failed catalogue cannot paint a false accusation.
     */
    notQuickQuoteCapable?: boolean;

    /** Same resolution as {@link CwStepRow#productLabel}, and for the same reason. */
    productLabel?: string;
}

const CW_CASE_TYPE_STEP = 'cw.CwCaseTypeStep';
const CW_CASE_TYPE_PRODUCT = 'cw.CwCaseTypeProduct';
const CW_CASE_TYPE_QUICK_QUOTE_STEP = 'cw.CwCaseTypeQuickQuoteStep';
const CW_STEP_TYPE = 'cw.CwStepType';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Modellierung" navigation point of the CwCaseType standard view — renders the case
 *   type's CwCaseTypeStep plan as a vertical timeline (ordered by stepOrder, CASE/PRODUKT level
 *   tags, monospace stepKey, product-binding/optional/visibility-logic/UF-flow badges). Selecting or
 *   editing a step opens the dedicated CwCaseTypeStepObjectComponent on the right via the Navigation
 *   Service; the timeline reloads automatically when that drawer form closes.
 */
@Component({
    selector: 'cw-case-type-modeling',
    templateUrl: './cw-case-type-modeling.component.html',
    styleUrls: ['./cw-case-type-modeling.component.scss'],
    standalone: false
})
export class CwCaseTypeModelingComponent implements OnInit, OnChanges, OnDestroy {

    @Input() caseTypeId: number;

    readonly CwStepLevelEnum = CwStepLevelEnum;

    steps: CwStepRow[] = [];
    loading: boolean = false;
    selectedStep: CwStepRow;

    /** The QUICK QUOTE stack of this case type — often a subset of the steps above, but freely chosen. */
    quickQuoteSteps: CwQuickQuoteStepRow[] = [];
    quickQuoteLoading: boolean = false;
    selectedQuickQuoteStep: CwQuickQuoteStepRow;

    private navigationSubscription: Subscription;
    /** True while a CwCaseTypeStep form is open in the right drawer — drives the refresh-on-close. */
    private stepFormOpen: boolean = false;
    /** The same, for the Quick-Quote sister entity — the two drawers refresh their own section. */
    private quickQuoteFormOpen: boolean = false;

    /** {@code CwCaseTypeProduct} id -> readable label, for the product-binding badge. */
    private productLabels = new Map<number, string>();

    /** Whether the product-line read has ANSWERED (either way). Gates the label fallback. */
    private productLinesLoaded: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService) {
    }

    ngOnInit(): void {
        // Lines before steps: loadProductLines closes the label gate as its first act, so a step read
        // that answers first cannot be labelled from a map that belongs to the previous case type.
        this.loadProductLines();
        this.loadSteps();
        this.loadQuickQuoteSteps();
        this.setupNavigationRefresh();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.selectedStep = null;
            this.selectedQuickQuoteStep = null;
            this.loadProductLines();
            this.loadSteps();
            this.loadQuickQuoteSteps();
        }
    }

    ngOnDestroy(): void {
        this.navigationSubscription?.unsubscribe();
    }

    loadSteps(): void {
        const service = this.coreService.getCrudService(CW_CASE_TYPE_STEP);
        if (!service) {
            return;
        }
        this.loading = true;
        const request = new ObjectRequestList(
            false,
            [FilterCriteria.create('caseType', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            [new Sorting('stepOrder', true)]
        );
        service.list(request).subscribe({
            next: (result: any) => {
                this.steps = (result?.entries ?? []) as CwStepRow[];
                this.applyProductLabels();
                this.loading = false;
            },
            error: () => {
                this.steps = [];
                this.loading = false;
            }
        });
    }

    /**
     * W17 — the product lines of this case type, so a PRODUCT step's pin can be shown by NAME.
     *
     * <p>A separate read rather than a nested node on the step list: the pin is an FK on the step,
     * and the ~24 pinned steps of a case type point at a handful of lines, so one read of the lines
     * beats resolving the same few rows once per step. The lines are read WITH their {@code product}
     * nested, because {@code CwCaseTypeProduct.name} is nullable — the product's own name is then the
     * label, and neither of the two flat {@code *DtoName} fields can serve (both resolve to the id).
     */
    private loadProductLines(): void {
        // Closed BEFORE the read is even issued. On a case-type switch the previous type's map is
        // still populated and its ids overlap the new type's, so labelling from it while the read is
        // outstanding would name a binding this case type does not have — stated with full
        // confidence. The map itself is replaced when the answer arrives; this flag is what keeps it
        // from being used in between.
        this.productLinesLoaded = false;

        const service = this.coreService.getCrudService(CW_CASE_TYPE_PRODUCT);
        if (!service) {
            this.productLinesLoaded = true;
            this.applyProductLabels();
            return;
        }

        const request = new ObjectRequestList(
            false,
            [FilterCriteria.create('caseType', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            [new Sorting('id', true)]
        );
        request.objectRequestComplex = ObjectRequestComplex.build(
            false,
            false,
            ObjectRequestComplexNode.createSimpleAttributeNode('product'),
        );

        service.list(request).subscribe({
            next: (result: any) => {
                this.productLabels.clear();
                for (const line of result?.entries ?? []) {
                    this.productLabels.set(
                        line.id,
                        line.name || line.productDto?.name || ('Produkt #' + line.id));
                }
                this.productLinesLoaded = true;
                this.applyProductLabels();
            },
            error: () => {
                // The pins are still shown — by id, which at least distinguishes steps that a
                // failed read would otherwise leave looking identical.
                this.productLabels.clear();
                this.productLinesLoaded = true;
                this.applyProductLabels();
            }
        });
    }

    /**
     * Writes the pin label onto every step. Runs after EITHER read, because the two are independent
     * and neither order is guaranteed; until the lines have answered the label stays unset, so the
     * badge appears resolved instead of flashing "Produkt #42" first.
     */
    private applyProductLabels(): void {
        if (!this.productLinesLoaded) {
            return;
        }
        for (const step of this.steps) {
            step.productLabel = step.caseTypeProductDtoId
                ? (this.productLabels.get(step.caseTypeProductDtoId)
                    || ('Produkt #' + step.caseTypeProductDtoId))
                : null;
        }
        for (const step of this.quickQuoteSteps) {
            step.productLabel = step.caseTypeProductDtoId
                ? (this.productLabels.get(step.caseTypeProductDtoId)
                    || ('Produkt #' + step.caseTypeProductDtoId))
                : null;
        }
    }

    // ============================================================================================
    // Quick Quote
    // ============================================================================================

    /**
     * Loads the case type's Quick-Quote stack, with the step TYPE resolved by name.
     *
     * <p>The step type is shown because the Quick Quote view reads its component from THERE and from
     * nowhere else — the runtime step plan is not consulted at all — so „welcher Schritttyp" is the
     * single decision that determines what a Quick-Quote step renders. The catalogue is read
     * separately for the same reason the product lines are: a handful of types serve every row.</p>
     *
     * <p>INACTIVE rows are listed too, unlike the Quick Quote view itself, which only renders active
     * ones. A configuration screen that hid what it configures would make a deactivated step look
     * deleted.</p>
     */
    loadQuickQuoteSteps(): void {
        const service = this.coreService.getCrudService(CW_CASE_TYPE_QUICK_QUOTE_STEP);
        if (!service) {
            return;
        }
        this.quickQuoteLoading = true;
        const request = new ObjectRequestList(
            false,
            [FilterCriteria.create('caseType', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            [new Sorting('stepOrder', true)]
        );
        service.list(request).subscribe({
            next: (result: any) => {
                this.quickQuoteSteps = (result?.entries ?? []) as CwQuickQuoteStepRow[];
                this.applyProductLabels();
                this.loadQuickQuoteStepTypeLabels();
                this.quickQuoteLoading = false;
            },
            error: () => {
                this.quickQuoteSteps = [];
                this.quickQuoteLoading = false;
            }
        });
    }

    /**
     * Resolves the referenced step types by NAME, in one batched read — and, from the SAME read,
     * decides per row whether the Quick Quote can actually run that step type.
     *
     * <p>Both come from the step type's own row ({@code name} for the badge, {@code componentName} for
     * the verdict), so they are answered together rather than by a second read that could disagree
     * with the first. {@code isQuickQuoteOfferableStepComponent} is the SAME rule the editor's picker
     * offers by, which is what makes "not offered" and "warned about here" two views of one decision
     * instead of two lists that drift.</p>
     */
    private loadQuickQuoteStepTypeLabels(): void {
        const service = this.coreService.getCrudService(CW_STEP_TYPE);
        const ids = Array.from(new Set(
            this.quickQuoteSteps.map(step => step.stepTypeDtoId).filter(id => id != null)));
        if (!service || !ids.length) {
            return;
        }

        const request = new ObjectRequestList(
            false,
            [FilterCriteria.create('id', FilterCriteria.cOperatorIn, ids)],
            []
        );

        service.list(request).subscribe({
            next: (result: any) => {
                const labels = new Map<number, string>();
                const capable = new Map<number, boolean>();
                for (const type of result?.entries ?? []) {
                    if (type?.id != null) {
                        labels.set(type.id, type.name || type.alias || ('Schritttyp #' + type.id));
                        capable.set(type.id, isQuickQuoteOfferableStepComponent(type.componentName));
                    }
                }
                for (const step of this.quickQuoteSteps) {
                    step.stepTypeLabel = labels.get(step.stepTypeDtoId) ?? null;
                    // A step type the catalogue does not contain at all is NOT flagged: the read
                    // answered about the ids it was asked for, and "absent" is a different defect
                    // (a dangling reference) than "present but not capturable".
                    step.notQuickQuoteCapable = capable.has(step.stepTypeDtoId)
                        ? !capable.get(step.stepTypeDtoId)
                        : undefined;
                }
            },
            error: () => {
                // The rows stay — without a type name, which at least still distinguishes them by
                // their own name and key. Failing the whole section over a label would be worse.
                // The capability verdict is CLEARED rather than defaulted: a failed read knows
                // nothing, and a warning painted from nothing is worse than no warning.
                for (const step of this.quickQuoteSteps) {
                    step.stepTypeLabel = null;
                    step.notQuickQuoteCapable = undefined;
                }
            }
        });
    }

    /** Row click — highlights the Quick-Quote step and opens its editor in the right drawer. */
    selectQuickQuoteStep(step: CwQuickQuoteStepRow): void {
        this.selectedQuickQuoteStep = step;
        this.openQuickQuoteStep(step);
    }

    /** Pencil action — opens the Quick-Quote step's editor in the right drawer. */
    editQuickQuoteStep(step: CwQuickQuoteStepRow, event: Event): void {
        event.stopPropagation();
        this.selectedQuickQuoteStep = step;
        this.openQuickQuoteStep(step);
    }

    private openQuickQuoteStep(step: CwQuickQuoteStepRow): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            CW_CASE_TYPE_QUICK_QUOTE_STEP,
            step.id,
            'object',
            step.name || step.stepKey,
            '',
            '',
            MvsObjectNavigationActionEnum.edit,
            'side'
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    /** „Quick-Quote-Schritt hinzufügen" — the create form with the case type preset as context. */
    addQuickQuoteStep(): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            CW_CASE_TYPE_QUICK_QUOTE_STEP,
            null,
            'object',
            'Neuer Quick-Quote-Schritt',
            '',
            '',
            MvsObjectNavigationActionEnum.create,
            'side'
        );
        entry.context = [new ObjectIdentifier('cw.CwCaseType', this.caseTypeId)];
        this.navigationService.navigateTo(entry, 'right');
    }

    /** Row click — highlights the step and opens its dedicated object component in the right drawer. */
    selectStep(step: CwStepRow): void {
        this.selectedStep = step;
        this.openStep(step);
    }

    /** Pencil action — opens the step's dedicated object component in the right drawer. */
    editStep(step: CwStepRow, event: Event): void {
        event.stopPropagation();
        this.selectedStep = step;
        this.openStep(step);
    }

    private openStep(step: CwStepRow): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            CW_CASE_TYPE_STEP,
            step.id,
            'object',
            step.name,
            '',
            '',
            MvsObjectNavigationActionEnum.edit,
            'side'
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    /** „+ Schritt hinzufügen" — opens the create form in the right drawer with the case type as context. */
    addStep(): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            CW_CASE_TYPE_STEP,
            null,
            'object',
            'Neuer Schritt',
            '',
            '',
            MvsObjectNavigationActionEnum.create,
            'side'
        );
        entry.context = [new ObjectIdentifier('cw.CwCaseType', this.caseTypeId)];
        this.navigationService.navigateTo(entry, 'right');
    }

    /**
     * Refreshes the timeline after a step drawer form closes — mirrors the node view's
     * setupNavigationSubscription: track when a CwCaseTypeStep form opens in the right sidebar, and
     * reload the steps once the entry becomes null (the form was closed after a create/edit).
     */
    private setupNavigationRefresh(): void {
        this.navigationSubscription = this.navigationService.navigationObjectSideBarRight.subscribe(
            (entry: MvsObjectNavigationEntry) => {
                if (!entry) {
                    if (this.stepFormOpen) {
                        this.stepFormOpen = false;
                        this.loadSteps();
                    }
                    // Tracked separately from the regular step form so a closed Quick-Quote drawer
                    // refreshes ITS section and does not re-read the other one for nothing.
                    if (this.quickQuoteFormOpen) {
                        this.quickQuoteFormOpen = false;
                        this.loadQuickQuoteSteps();
                    }
                } else if (entry.objectIdentifier?.objectType === CW_CASE_TYPE_STEP) {
                    this.stepFormOpen = true;
                } else if (entry.objectIdentifier?.objectType === CW_CASE_TYPE_QUICK_QUOTE_STEP) {
                    this.quickQuoteFormOpen = true;
                }
            }
        );
    }
}
